import {
  Cloud,
  CloudOff,
  Link2,
  Monitor,
  Pencil,
  RefreshCw,
  RotateCw,
  Send,
  ScanLine,
  ShieldOff,
  TerminalSquare,
  X,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from "react";
import type {
  BrowserSessionWireframe,
  SlideFieldValues,
  SlideTypeDefinition,
} from "@qassist/schemas";
import { SlideRenderer } from "@qassist/slide-renderer";
import { ApiError, api } from "./api.js";
import { shuffledPlaylistItems } from "./playerSlideKey.js";
import { browserZoomStyle, playerLogicalViewport } from "./playerZoom.js";
import { useModalFocus } from "./useModalFocus.js";

type CommandType =
  | "read_diagnostics"
  | "identify_device"
  | "update_settings"
  | "update_device_access"
  | "connect_wifi"
  | "open_wifi_settings"
  | "open_android_settings"
  | "show_launcher_selection"
  | "reload_player"
  | "restart_app"
  | "check_apk_update"
  | "install_tailscale"
  | "open_tailscale"
  | "authorize_tailscale"
  | "set_tailscale_always_on"
  | "reboot";

interface CloudCommand {
  id: string;
  type: CommandType;
  payload: Record<string, unknown>;
  status:
    "queued" | "delivered" | "acknowledged" | "succeeded" | "failed" | "expired" | "cancelled";
  attempts: number;
  errorCode: string | null;
  result: { message?: string; diagnostics?: Record<string, unknown> } | null;
  createdAt: string;
  expiresAt: string;
}

interface CloudTv {
  id: string;
  clientName: string;
  name: string;
  deviceName: string | null;
  status: string;
  installationId: string | null;
  cloudStatus: "online" | "offline" | "not enrolled" | "disabled";
  lastContactAt: string | null;
  agentVersion: string | null;
  apkVersion: string | null;
  androidVersion: string | null;
  updateStatus: string | null;
  diagnostics: Record<string, unknown>;
  commands: CloudCommand[];
}

interface PendingCloudDevice {
  id: string;
  deviceId: string;
  deviceName: string | null;
  agentVersion: string;
  apkVersion: string | null;
  androidVersion: string | null;
  updateStatus: string | null;
  diagnostics: Record<string, unknown>;
  commands: CloudCommand[];
  connectionStatus: "online" | "offline";
  createdAt: string;
  lastContactAt: string;
  expiresAt: string;
}

interface BrowserPlayerSession {
  id: string;
  deviceName: string | null;
  installationId: string | null;
  userAgent: string;
  screenWidth: number | null;
  screenHeight: number | null;
  createdAt: string;
  lastContactAt: string;
  connectionStatus: "online" | "offline";
  status: "claimed" | "unclaimed";
  tvId: string | null;
  tvName: string | null;
  clientId: string | null;
  clientName: string | null;
}

interface DevicePreviewItem {
  id: string;
  slideId: string;
  name: string;
  values: SlideFieldValues;
  definition: SlideTypeDefinition;
  durationMs: number;
}

interface DevicePreviewConfiguration {
  tv: {
    id: string;
    name: string;
    timezone?: string;
    orientation?: "landscape" | "portrait";
    resolutionWidth?: number;
    resolutionHeight?: number;
    zoomPercent?: number;
    backgroundColor?: string;
    backgroundUrl?: string | null;
  };
  clientStatus: string;
  policy: string;
  configurationVersion: number;
  cache: { namespace: string };
  playlist: {
    id: string | null;
    shuffle: boolean;
    overlay: {
      slideId: string;
      name: string;
      values: SlideFieldValues;
      definition: SlideTypeDefinition;
    } | null;
    items: DevicePreviewItem[];
  } | null;
}

interface PreviewTarget {
  tvId: string;
  name: string;
  description: string;
  reportedViewport?: {
    width: number;
    height: number;
  };
  reportedViewportLabel?: "Session" | "Last reported";
}

interface WireframeResult {
  requestId: string | null;
  requestedAt: string | null;
  deliveredAt: string | null;
  capturedAt: string | null;
  status: "idle" | "pending" | "delivered" | "ready";
  wireframe: BrowserSessionWireframe | null;
}

interface WireframeTarget {
  id: string;
  name: string;
}

const commandLabels: Record<CommandType, string> = {
  read_diagnostics: "Read diagnostics",
  identify_device: "Identify device",
  update_settings: "Update device settings",
  update_device_access: "Update device access",
  connect_wifi: "Connect to Wi-Fi",
  open_wifi_settings: "Open Wi-Fi settings",
  open_android_settings: "Open Android settings",
  show_launcher_selection: "Show launcher selection",
  reload_player: "Reload player",
  restart_app: "Restart Q-Assist",
  check_apk_update: "Check APK update",
  install_tailscale: "Download and install Tailscale",
  open_tailscale: "Open Tailscale",
  authorize_tailscale: "Authorize Tailscale with generated code",
  set_tailscale_always_on: "Set Tailscale always-on VPN",
  reboot: "Reboot device",
};

function Modal({
  title,
  description,
  onClose,
  children,
}: {
  title: string;
  description: string;
  onClose(): void;
  children: ReactNode;
}) {
  const dialogRef = useModalFocus(true, onClose);
  return (
    <div className="modal-layer" role="presentation">
      <button
        className="modal-scrim"
        onClick={onClose}
        aria-label={`Close ${title}`}
        tabIndex={-1}
      />
      <section
        ref={dialogRef}
        className="modal modal--large"
        role="dialog"
        aria-modal="true"
        aria-labelledby="device-cloud-dialog-title"
        tabIndex={-1}
      >
        <header className="modal-head">
          <div>
            <h2 id="device-cloud-dialog-title">{title}</h2>
            <p>{description}</p>
          </div>
          <button
            className="icon-button"
            onClick={onClose}
            aria-label={`Close ${title}`}
            data-modal-initial-focus
          >
            <X size={20} />
          </button>
        </header>
        <div className="modal-body">{children}</div>
      </section>
    </div>
  );
}

function formatContact(value: string | null): string {
  return value ? new Date(value).toLocaleString() : "Never";
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.message : "The request failed. Try again.";
}

export function BrowserSessionWireframePreview({
  wireframe,
}: {
  wireframe: BrowserSessionWireframe;
}) {
  const strokeWidth = Math.max(2, Math.round(wireframe.viewport.width / 700));
  const fontSize = Math.max(14, Math.round(wireframe.viewport.width / 85));
  return (
    <section className="browser-wireframe" aria-label="Browser session wireframe">
      <div className="browser-wireframe__screen">
        <svg
          viewBox={`0 0 ${wireframe.viewport.width} ${wireframe.viewport.height}`}
          role="img"
          aria-label={`${wireframe.state} browser viewport with ${wireframe.nodes.length} visible elements`}
          preserveAspectRatio="xMidYMid meet"
        >
          <rect
            className="browser-wireframe__viewport"
            x="0"
            y="0"
            width={wireframe.viewport.width}
            height={wireframe.viewport.height}
            strokeWidth={strokeWidth}
          />
          {wireframe.canvases.map((canvas) => (
            <rect
              key={canvas.layer}
              className={`browser-wireframe__canvas browser-wireframe__canvas--${canvas.layer}`}
              x={canvas.x}
              y={canvas.y}
              width={canvas.width}
              height={canvas.height}
              strokeWidth={strokeWidth}
            />
          ))}
          {wireframe.nodes.map((node) => (
            <g key={`${node.layer}-${node.index}`}>
              <rect
                className={`browser-wireframe__node browser-wireframe__node--${node.layer} browser-wireframe__node--${node.type}`}
                x={node.x}
                y={node.y}
                width={node.width}
                height={node.height}
                strokeWidth={strokeWidth}
              />
              {node.width >= fontSize * 3 && node.height >= fontSize * 1.5 ? (
                <text
                  className="browser-wireframe__node-label"
                  x={node.x + fontSize * 0.35}
                  y={node.y + fontSize * 1.15}
                  fontSize={fontSize}
                >
                  {node.type}
                </text>
              ) : null}
            </g>
          ))}
        </svg>
      </div>
      <footer className="browser-wireframe__status" aria-live="polite">
        <span>
          <strong>{wireframe.state}</strong> · {wireframe.nodes.length} visible element
          {wireframe.nodes.length === 1 ? "" : "s"}
        </span>
        <span>
          {wireframe.viewport.width} × {wireframe.viewport.height} · captured{" "}
          {formatContact(wireframe.capturedAt)}
        </span>
      </footer>
      <div className="browser-wireframe__legend" aria-label="Wireframe legend">
        <span>
          <i className="browser-wireframe__key browser-wireframe__key--slide" /> Slide
        </span>
        <span>
          <i className="browser-wireframe__key browser-wireframe__key--overlay" /> Overlay
        </span>
        <span>No text, images, URLs, video pixels, or form values are collected.</span>
      </div>
    </section>
  );
}

function diagnosticText(
  diagnostics: Record<string, unknown>,
  key: string,
  fallback = "Unavailable",
): string {
  const value = diagnostics[key];
  return typeof value === "string" || typeof value === "number" || typeof value === "boolean"
    ? String(value)
    : fallback;
}

export function DeviceLivePreview({
  configuration,
  reportedViewport,
  reportedViewportLabel = "Session",
}: {
  configuration: DevicePreviewConfiguration;
  reportedViewport?: {
    width: number;
    height: number;
  };
  reportedViewportLabel?: "Session" | "Last reported";
}) {
  const screenRef = useRef<HTMLDivElement>(null);
  const [fitScale, setFitScale] = useState<number | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const itemKey = (configuration.playlist?.items ?? []).map((item) => item.id).join("|");
  const items = useMemo(() => {
    const sourceItems = configuration.playlist?.items ?? [];
    return configuration.playlist?.shuffle ? shuffledPlaylistItems(sourceItems) : sourceItems;
  }, [configuration.playlist?.shuffle, itemKey]);
  const current = items[currentIndex % Math.max(1, items.length)];
  const configuredViewport = {
    width:
      configuration.tv.resolutionWidth ??
      (configuration.tv.orientation === "portrait" ? 1080 : 1920),
    height:
      configuration.tv.resolutionHeight ??
      (configuration.tv.orientation === "portrait" ? 1920 : 1080),
  };
  const viewport = reportedViewport ?? configuredViewport;
  const logicalViewport = playerLogicalViewport(
    viewport.width,
    viewport.height,
    current?.definition.canvas.width ??
      configuration.playlist?.overlay?.definition.canvas.width ??
      1960,
  );

  useEffect(() => {
    setCurrentIndex(0);
  }, [configuration.cache.namespace, configuration.playlist?.id]);

  useEffect(() => {
    if (!current || items.length < 2) return;
    const timer = window.setTimeout(
      () => setCurrentIndex((index) => (index + 1) % items.length),
      Math.max(1_000, current.durationMs),
    );
    return () => window.clearTimeout(timer);
  }, [current, items.length]);

  useEffect(() => {
    const screen = screenRef.current;
    if (!screen) return;
    const updateScale = (): void => {
      if (screen.clientWidth <= 0 || logicalViewport.width <= 0) return;
      setFitScale(screen.clientWidth / logicalViewport.width);
    };
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(screen);
    return () => observer.disconnect();
  }, [logicalViewport.height, logicalViewport.width]);

  const backgroundStyle = {
    backgroundColor: configuration.tv.backgroundColor ?? "#000000",
    backgroundImage: configuration.tv.backgroundUrl
      ? `url("${configuration.tv.backgroundUrl.replaceAll('"', "%22")}")`
      : undefined,
  } satisfies CSSProperties;

  return (
    <section
      className="device-live-preview"
      aria-label={`Configuration preview for ${configuration.tv.name}`}
    >
      <div
        ref={screenRef}
        className="device-live-preview__screen"
        style={{ ...backgroundStyle, aspectRatio: `${viewport.width} / ${viewport.height}` }}
      >
        <div
          className="device-live-preview__canvas-fit"
          style={{
            width: logicalViewport.width,
            height: logicalViewport.height,
            opacity: fitScale === null ? 0 : 1,
            transform: `scale(${fitScale ?? 1})`,
          }}
        >
          <div
            className="device-live-preview__zoom"
            style={browserZoomStyle(configuration.tv.zoomPercent ?? 100)}
          >
            {current ? (
              <SlideRenderer
                key={`${configuration.cache.namespace}:${current.id}`}
                definition={current.definition}
                values={current.values}
                orientation={configuration.tv.orientation ?? "landscape"}
                timezone={configuration.tv.timezone}
                className="device-live-preview__slide"
                ariaLabel={current.name}
                videoLoop
                videoMuted
              />
            ) : configuration.playlist?.overlay ? null : (
              <div className="device-live-preview__empty">
                <CloudOff size={32} />
                <strong>No scheduled slide is currently available</strong>
                <span>The preview will update automatically when content becomes active.</span>
              </div>
            )}
            {configuration.playlist?.overlay ? (
              <SlideRenderer
                definition={configuration.playlist.overlay.definition}
                values={configuration.playlist.overlay.values}
                orientation={configuration.tv.orientation ?? "landscape"}
                timezone={configuration.tv.timezone}
                className="device-live-preview__playlist-overlay"
                ariaLabel={configuration.playlist.overlay.name}
                videoLoop
                videoMuted
              />
            ) : null}
          </div>
        </div>
      </div>
      <footer className="device-live-preview__status" aria-live="polite">
        <span>
          {current ? (
            <>
              Showing <strong>{current.name}</strong> · slide{" "}
              {(currentIndex % Math.max(1, items.length)) + 1} of {items.length}
            </>
          ) : configuration.playlist?.overlay ? (
            <>
              Showing overlay <strong>{configuration.playlist.overlay.name}</strong>
            </>
          ) : (
            "Waiting for scheduled content"
          )}
        </span>
        <span>
          {reportedViewport ? (
            <>
              {reportedViewportLabel} {viewport.width} × {viewport.height} · configured{" "}
              {configuredViewport.width} × {configuredViewport.height} · configuration{" "}
              {configuration.configurationVersion}
            </>
          ) : (
            <>
              {configuredViewport.width} × {configuredViewport.height} · configuration{" "}
              {configuration.configurationVersion}
            </>
          )}
        </span>
      </footer>
    </section>
  );
}

export function DeviceCloudPage() {
  const queryClient = useQueryClient();
  const commandSubmissionPending = useRef(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pendingDeviceId, setPendingDeviceId] = useState<string | null>(null);
  const [pendingAdminId, setPendingAdminId] = useState<string | null>(null);
  const [pendingClaimMethod, setPendingClaimMethod] = useState<"tvId" | "pairingCode">("tvId");
  const [pendingClaimTvId, setPendingClaimTvId] = useState("");
  const [pendingPairingCode, setPendingPairingCode] = useState("");
  const [pendingTransfer, setPendingTransfer] = useState(false);
  const [assignBrowserSessionId, setAssignBrowserSessionId] = useState<string | null>(null);
  const [assignBrowserTvId, setAssignBrowserTvId] = useState("");
  const [revokeBrowserSessionId, setRevokeBrowserSessionId] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<{
    kind: "tv" | "pending" | "browser";
    id: string;
    deviceId: string;
    name: string;
  } | null>(null);
  const [deviceName, setDeviceName] = useState("");
  const [previewTarget, setPreviewTarget] = useState<PreviewTarget | null>(null);
  const [wireframeTarget, setWireframeTarget] = useState<WireframeTarget | null>(null);
  const [wireframeRequestId, setWireframeRequestId] = useState<string | null>(null);
  const [claimCode, setClaimCode] = useState("");
  const [reconnectDeviceId, setReconnectDeviceId] = useState("");
  const [reconnectConfirmation, setReconnectConfirmation] = useState("");
  const [commandType, setCommandType] = useState<CommandType>("read_diagnostics");
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState({
    homeUrl: "",
    updateMetadataUrl: "",
    updateApkUrl: "",
    panelPort: "",
    hotspotSsid: "",
    hotspotPassword: "",
    wifiSsid: "",
    wifiPassword: "",
    allowAndroidSettings: "",
    allowUsbDebugging: "",
    allowAppUninstall: "",
    allowAppInstall: "",
    tailscaleAlwaysOn: "true",
  });
  const query = useQuery({
    queryKey: ["admin", "device-cloud"],
    queryFn: () => api<CloudTv[]>("/api/v1/admin/device-cloud"),
    refetchInterval: 5_000,
  });
  const pendingQuery = useQuery({
    queryKey: ["admin", "device-cloud", "pending"],
    queryFn: () => api<PendingCloudDevice[]>("/api/v1/admin/device-cloud/pending-enrollments"),
    refetchInterval: 5_000,
  });
  const browserSessionsQuery = useQuery({
    queryKey: ["admin", "device-cloud", "browser-sessions"],
    queryFn: () => api<BrowserPlayerSession[]>("/api/v1/admin/device-cloud/browser-sessions"),
    refetchInterval: 5_000,
  });
  const previewQuery = useQuery({
    queryKey: ["admin", "device-cloud", "preview", previewTarget?.tvId],
    queryFn: () =>
      api<DevicePreviewConfiguration>(
        `/api/v1/admin/device-cloud/tvs/${previewTarget?.tvId}/preview`,
      ),
    enabled: Boolean(previewTarget),
    refetchInterval: 5_000,
  });
  const wireframeQuery = useQuery({
    queryKey: [
      "admin",
      "device-cloud",
      "browser-wireframe",
      wireframeTarget?.id,
      wireframeRequestId,
    ],
    queryFn: () =>
      api<WireframeResult>(
        `/api/v1/admin/device-cloud/browser-sessions/${wireframeTarget?.id}/wireframe`,
      ),
    enabled: Boolean(wireframeTarget && wireframeRequestId),
    refetchInterval: (query) => (query.state.data?.status === "ready" ? false : 1_000),
  });
  const selected = query.data?.find((tv) => tv.id === selectedId) ?? null;
  const pendingDevice = pendingQuery.data?.find((device) => device.id === pendingDeviceId) ?? null;
  const pendingAdmin = pendingQuery.data?.find((device) => device.id === pendingAdminId) ?? null;
  const assigningBrowserSession =
    browserSessionsQuery.data?.find((session) => session.id === assignBrowserSessionId) ?? null;
  const revokingBrowserSession =
    browserSessionsQuery.data?.find((session) => session.id === revokeBrowserSessionId) ?? null;
  const remoteTarget = selected
    ? {
        kind: "tv" as const,
        id: selected.id,
        name: selected.name,
        description: `${selected.clientName} · ${selected.cloudStatus} · last contact ${formatContact(selected.lastContactAt)}`,
        status: selected.cloudStatus,
        diagnostics: selected.diagnostics,
        commands: selected.commands,
      }
    : pendingAdmin
      ? {
          kind: "pending" as const,
          id: pendingAdmin.id,
          name: `Unassigned · ${pendingAdmin.deviceId}`,
          description: `${pendingAdmin.connectionStatus} · last contact ${formatContact(pendingAdmin.lastContactAt)}`,
          status: pendingAdmin.connectionStatus,
          diagnostics: pendingAdmin.diagnostics,
          commands: pendingAdmin.commands,
        }
      : null;
  const refresh = (): Promise<unknown> =>
    queryClient.invalidateQueries({ queryKey: ["admin", "device-cloud"] });
  const claimPending = useMutation({
    mutationFn: () =>
      api<{ tvId: string; status: string }>(
        `/api/v1/admin/device-cloud/pending-enrollments/${pendingDeviceId}/claim`,
        {
          method: "POST",
          body: JSON.stringify(
            pendingClaimMethod === "tvId"
              ? { tvId: pendingClaimTvId, ...(pendingTransfer ? { transfer: true } : {}) }
              : {
                  pairingCode: pendingPairingCode.trim(),
                  ...(pendingTransfer ? { transfer: true } : {}),
                },
          ),
        },
      ),
    onSuccess: async ({ tvId, status }) => {
      setPendingDeviceId(null);
      setPendingAdminId(null);
      setPendingClaimTvId("");
      setPendingPairingCode("");
      setPendingTransfer(false);
      setNotice(
        status === "transferred"
          ? "Device transferred. The new APK installation will receive a fresh credential."
          : "Device claimed. The APK will connect to its assigned TV automatically.",
      );
      setSelectedId(tvId);
      await refresh();
    },
  });
  const claim = useMutation({
    mutationFn: () =>
      api(`/api/v1/admin/device-cloud/tvs/${selectedId}/enroll`, {
        method: "POST",
        body: JSON.stringify({ claimCode: claimCode.trim().toUpperCase() }),
      }),
    onSuccess: async () => {
      setClaimCode("");
      setNotice("Enrollment claimed. The TV will receive its cloud credential automatically.");
      await refresh();
    },
  });
  const queue = useMutation({
    mutationFn: ({
      payload,
      idempotencyKey,
    }: {
      payload: Record<string, unknown>;
      idempotencyKey: string;
    }) =>
      api<CloudCommand>(
        remoteTarget?.kind === "pending"
          ? `/api/v1/admin/device-cloud/pending-enrollments/${remoteTarget.id}/commands`
          : `/api/v1/admin/device-cloud/tvs/${selectedId}/commands`,
        {
          method: "POST",
          body: JSON.stringify({
            type: commandType,
            payload,
            idempotencyKey,
          }),
        },
      ),
    onSuccess: async (command) => {
      setNotice(
        command.status === "queued"
          ? "Command queued. It will be delivered when the TV polls."
          : `Command is ${command.status}.`,
      );
      setForm((current) => ({
        ...current,
        wifiPassword: "",
        hotspotPassword: "",
      }));
      await refresh();
    },
    onSettled: () => {
      commandSubmissionPending.current = false;
    },
  });
  const cancel = useMutation({
    mutationFn: (commandId: string) =>
      api(
        remoteTarget?.kind === "pending"
          ? `/api/v1/admin/device-cloud/pending-commands/${commandId}/cancel`
          : `/api/v1/admin/device-cloud/commands/${commandId}/cancel`,
        { method: "POST" },
      ),
    onSuccess: async () => {
      setNotice("Command cancelled.");
      await refresh();
    },
  });
  const rotate = useMutation({
    mutationFn: () =>
      api(`/api/v1/admin/device-cloud/tvs/${selectedId}/rotate`, { method: "POST" }),
    onSuccess: async () => {
      setNotice("Cloud credential rotated. The APK will retrieve it through enrollment.");
      await refresh();
    },
  });
  const revoke = useMutation({
    mutationFn: () =>
      api(`/api/v1/admin/device-cloud/tvs/${selectedId}/revoke`, { method: "POST" }),
    onSuccess: async () => {
      setNotice(
        "Cloud access revoked and the Device ID released. The APK will register again and appear under Unassigned APK devices after its next successful internet contact.",
      );
      await refresh();
    },
  });
  const reconnect = useMutation({
    mutationFn: () =>
      api(`/api/v1/admin/device-cloud/tvs/${selectedId}/reconnect`, {
        method: "POST",
        body: JSON.stringify({
          installationId: reconnectDeviceId.trim(),
          confirmation: reconnectConfirmation,
        }),
      }),
    onSuccess: async () => {
      setReconnectConfirmation("");
      setNotice(
        "Reconnect credential prepared. The matching APK will retrieve it through its bootstrap channel.",
      );
      await refresh();
    },
  });
  const assignBrowserSession = useMutation({
    mutationFn: () =>
      api<{ id: string; tvId: string; status: "assigned" | "reassigned" }>(
        `/api/v1/admin/device-cloud/browser-sessions/${assignBrowserSessionId}/assign`,
        {
          method: "POST",
          body: JSON.stringify({ tvId: assignBrowserTvId }),
        },
      ),
    onSuccess: async ({ status }) => {
      setNotice(
        status === "reassigned"
          ? "Browser session reassigned. Its active player will adopt the selected TV automatically."
          : "Browser session assigned. The player will connect automatically on its next heartbeat.",
      );
      setAssignBrowserSessionId(null);
      setAssignBrowserTvId("");
      await refresh();
    },
  });
  const revokeBrowserSession = useMutation({
    mutationFn: () =>
      api(`/api/v1/admin/device-cloud/browser-sessions/${revokeBrowserSessionId}/revoke`, {
        method: "POST",
      }),
    onSuccess: async () => {
      setNotice(
        "Browser session revoked. Its player credential is invalid and the browser must establish a new unclaimed session.",
      );
      setRevokeBrowserSessionId(null);
      await refresh();
    },
  });
  const renameDevice = useMutation({
    mutationFn: () => {
      if (!renameTarget) throw new Error("Choose a device to rename.");
      const path =
        renameTarget.kind === "tv"
          ? `/api/v1/admin/device-cloud/tvs/${renameTarget.id}`
          : renameTarget.kind === "pending"
            ? `/api/v1/admin/device-cloud/pending-enrollments/${renameTarget.id}`
            : `/api/v1/admin/device-cloud/browser-sessions/${renameTarget.id}`;
      return api(path, { method: "PATCH", body: JSON.stringify({ name: deviceName.trim() }) });
    },
    onSuccess: async () => {
      setNotice("Device name saved.");
      setRenameTarget(null);
      setDeviceName("");
      await refresh();
    },
  });
  const identifyDevice = useMutation({
    mutationFn: (target: { kind: "tv" | "pending" | "browser"; id: string; deviceId: string }) =>
      target.kind === "browser"
        ? api(`/api/v1/admin/device-cloud/browser-sessions/${target.id}/identify`, {
            method: "POST",
          })
        : api<CloudCommand>(
            target.kind === "pending"
              ? `/api/v1/admin/device-cloud/pending-enrollments/${target.id}/commands`
              : `/api/v1/admin/device-cloud/tvs/${target.id}/commands`,
            {
              method: "POST",
              body: JSON.stringify({
                type: "identify_device",
                payload: { deviceId: target.deviceId },
                idempotencyKey: crypto.randomUUID(),
              }),
            },
          ),
    onSuccess: async () => {
      setNotice(
        "Identify request sent. The Device ID will appear at the top-left for two seconds.",
      );
      await refresh();
    },
  });
  const requestWireframe = useMutation({
    mutationFn: (target: WireframeTarget) =>
      api<{ requestId: string; requestedAt: string; status: "pending" }>(
        `/api/v1/admin/device-cloud/browser-sessions/${target.id}/wireframe-requests`,
        { method: "POST" },
      ),
    onSuccess: ({ requestId }) => {
      setWireframeRequestId(requestId);
    },
  });

  function openWireframe(target: WireframeTarget): void {
    setWireframeTarget(target);
    setWireframeRequestId(null);
    setNotice("");
    requestWireframe.mutate(target);
  }

  function closeWireframe(): void {
    setWireframeTarget(null);
    setWireframeRequestId(null);
    requestWireframe.reset();
  }

  function openRename(target: {
    kind: "tv" | "pending" | "browser";
    id: string;
    deviceId: string;
    name: string | null;
  }): void {
    setRenameTarget({ ...target, name: target.name ?? "" });
    setDeviceName(target.name ?? "");
    setNotice("");
  }

  function submitCommand(event: FormEvent): void {
    event.preventDefault();
    if (commandSubmissionPending.current) return;
    let payload: Record<string, unknown> = {};
    if (commandType === "connect_wifi")
      payload = { ssid: form.wifiSsid.trim(), password: form.wifiPassword };
    if (commandType === "update_settings") {
      if (form.homeUrl.trim()) payload.homeUrl = form.homeUrl.trim();
      if (form.updateMetadataUrl.trim()) payload.updateMetadataUrl = form.updateMetadataUrl.trim();
      if (form.updateApkUrl.trim()) payload.updateApkUrl = form.updateApkUrl.trim();
      if (form.panelPort) payload.panelPort = Number(form.panelPort);
      if (form.hotspotSsid.trim()) payload.hotspotSsid = form.hotspotSsid.trim();
      if (form.hotspotPassword) payload.hotspotPassword = form.hotspotPassword;
    }
    if (commandType === "update_device_access") {
      for (const key of [
        "allowAndroidSettings",
        "allowUsbDebugging",
        "allowAppUninstall",
        "allowAppInstall",
      ] as const)
        if (form[key]) payload[key] = form[key] === "true";
    }
    if (commandType === "set_tailscale_always_on")
      payload.enabled = form.tailscaleAlwaysOn === "true";
    setNotice("");
    commandSubmissionPending.current = true;
    queue.mutate({ payload, idempotencyKey: crypto.randomUUID() });
  }

  const activeError =
    claimPending.error ??
    claim.error ??
    queue.error ??
    cancel.error ??
    rotate.error ??
    revoke.error ??
    reconnect.error ??
    assignBrowserSession.error ??
    revokeBrowserSession.error ??
    renameDevice.error ??
    identifyDevice.error ??
    null;

  return (
    <div className="page-stack device-cloud-page">
      <section className="page-actions">
        <div>
          <h2>Q-Assist Device Cloud</h2>
          <p>
            Monitor and control enrolled TVs through outbound authenticated HTTPS polling. No public
            TV address, VPN, or router forwarding is required.
          </p>
        </div>
        <button
          className="button button--secondary"
          type="button"
          onClick={() => {
            void query.refetch();
            void pendingQuery.refetch();
            void browserSessionsQuery.refetch();
          }}
          disabled={query.isFetching || pendingQuery.isFetching || browserSessionsQuery.isFetching}
        >
          <RefreshCw size={17} /> Refresh
        </button>
      </section>

      {query.isLoading ? <section className="card">Loading Device Cloud…</section> : null}
      {query.error ? <p className="notice error">{errorMessage(query.error)}</p> : null}
      {pendingQuery.error ? (
        <p className="notice error">{errorMessage(pendingQuery.error)}</p>
      ) : null}
      {browserSessionsQuery.error ? (
        <p className="notice error">{errorMessage(browserSessionsQuery.error)}</p>
      ) : null}
      {notice && !remoteTarget ? <p className="notice success">{notice}</p> : null}
      {activeError && !remoteTarget && !renameTarget ? (
        <p className="notice error">{errorMessage(activeError)}</p>
      ) : null}

      <section className="card table-card">
        <div className="section-heading">
          <div>
            <h3>Unassigned APK devices</h3>
            <p>
              Fresh Q-Assist installations appear here automatically as soon as they can reach the
              internet. Claim one to an existing TV or its six-digit pairing code; claiming also
              completes “Connect this display” automatically.
            </p>
          </div>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Device name</th>
                <th>Device ID</th>
                <th>Connection</th>
                <th>Agent</th>
                <th>First contact</th>
                <th>Last contact</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {(pendingQuery.data ?? []).map((device) => (
                <tr key={device.id}>
                  <td>
                    <strong>{device.deviceName ?? "Unnamed device"}</strong>
                  </td>
                  <td>
                    <code>{device.deviceId}</code>
                  </td>
                  <td>
                    <span className={`status-pill status-${device.connectionStatus}`}>
                      {device.connectionStatus === "online" ? (
                        <Cloud size={14} />
                      ) : (
                        <CloudOff size={14} />
                      )}
                      {device.connectionStatus}
                    </span>
                  </td>
                  <td>{device.agentVersion}</td>
                  <td>{formatContact(device.createdAt)}</td>
                  <td>{formatContact(device.lastContactAt)}</td>
                  <td>
                    <div className="action-row">
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() =>
                          openRename({
                            kind: "pending",
                            id: device.id,
                            deviceId: device.deviceId,
                            name: device.deviceName,
                          })
                        }
                        aria-label={`Rename device ${device.deviceId}`}
                      >
                        <Pencil size={16} /> Name
                      </button>
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() =>
                          identifyDevice.mutate({
                            kind: "pending",
                            id: device.id,
                            deviceId: device.deviceId,
                          })
                        }
                        disabled={identifyDevice.isPending}
                        aria-label={`Identify device ${device.deviceId}`}
                      >
                        <ScanLine size={16} /> Identify
                      </button>
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() => {
                          setPendingAdminId(device.id);
                          setSelectedId(null);
                          setNotice("");
                        }}
                        aria-label={`Remote Admin for unassigned device ${device.deviceId}`}
                      >
                        <TerminalSquare size={16} /> Remote Admin
                      </button>
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() => {
                          setPendingDeviceId(device.id);
                          setPendingClaimMethod("tvId");
                          setPendingClaimTvId("");
                          setPendingPairingCode("");
                          setPendingTransfer(false);
                          setNotice("");
                        }}
                        aria-label={`Claim device ${device.deviceId}`}
                      >
                        <Cloud size={16} /> Claim device
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!pendingQuery.isLoading && !(pendingQuery.data ?? []).length ? (
            <p className="empty-copy">No unassigned APK devices are available.</p>
          ) : null}
        </div>
      </section>

      <section className="card table-card">
        <div className="section-heading">
          <div>
            <h3>Browser sessions</h3>
            <p>
              Browser-based players appear here whether they are claimed or unclaimed. Assign or
              reassign a session to an existing TV without requiring an APK, or revoke that browser
              and its player access.
            </p>
          </div>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Device name</th>
                <th>Device ID</th>
                <th>Connection</th>
                <th>Assignment</th>
                <th>Screen size</th>
                <th>Browser</th>
                <th>First seen</th>
                <th>Last active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {(browserSessionsQuery.data ?? []).map((session) => (
                <tr key={session.id}>
                  <td>
                    <strong>{session.deviceName ?? "Unnamed browser"}</strong>
                  </td>
                  <td>
                    <code>{session.id}</code>
                    <small className="cell-subtitle">{session.status}</small>
                    {session.installationId ? (
                      <small className="cell-subtitle">
                        APK <code>{session.installationId}</code>
                      </small>
                    ) : null}
                  </td>
                  <td>
                    <span className={`status-pill status-${session.connectionStatus}`}>
                      {session.connectionStatus === "online" ? (
                        <Cloud size={14} />
                      ) : (
                        <CloudOff size={14} />
                      )}
                      {session.connectionStatus}
                    </span>
                  </td>
                  <td>
                    {session.tvName ? (
                      <>
                        <strong>{session.tvName}</strong>
                        <small className="cell-subtitle">{session.clientName}</small>
                      </>
                    ) : (
                      <span className="status-pill status-not-enrolled">unclaimed</span>
                    )}
                  </td>
                  <td>
                    {session.screenWidth && session.screenHeight
                      ? `${session.screenWidth} × ${session.screenHeight}`
                      : "Not reported"}
                  </td>
                  <td>
                    <span className="browser-session-agent" title={session.userAgent}>
                      {session.userAgent || "Browser details unavailable"}
                    </span>
                  </td>
                  <td>{formatContact(session.createdAt)}</td>
                  <td>{formatContact(session.lastContactAt)}</td>
                  <td>
                    <div className="action-row">
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() =>
                          openRename({
                            kind: "browser",
                            id: session.id,
                            deviceId: session.id,
                            name: session.deviceName,
                          })
                        }
                        aria-label={`Rename browser session ${session.id}`}
                      >
                        <Pencil size={16} /> Name
                      </button>
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() =>
                          identifyDevice.mutate({
                            kind: "browser",
                            id: session.id,
                            deviceId: session.installationId ?? session.id,
                          })
                        }
                        disabled={identifyDevice.isPending}
                        aria-label={`Identify browser session ${session.id}`}
                      >
                        <ScanLine size={16} /> Identify
                      </button>
                      {session.connectionStatus === "online" ? (
                        <button
                          type="button"
                          className="button button--secondary button--small"
                          onClick={() =>
                            openWireframe({
                              id: session.id,
                              name: session.deviceName ?? session.id,
                            })
                          }
                          aria-label={`Request wireframe from browser session ${session.id}`}
                        >
                          <Monitor size={16} /> Wireframe
                        </button>
                      ) : null}
                      {session.status === "claimed" && session.tvId && session.tvName ? (
                        <button
                          type="button"
                          className="button button--secondary button--small"
                          onClick={() =>
                            setPreviewTarget({
                              tvId: session.tvId!,
                              name: session.tvName!,
                              description: `${session.clientName ?? "Assigned client"} · browser session ${session.id}`,
                              ...(session.screenWidth && session.screenHeight
                                ? {
                                    reportedViewport: {
                                      width: session.screenWidth,
                                      height: session.screenHeight,
                                    },
                                    reportedViewportLabel:
                                      session.connectionStatus === "online"
                                        ? ("Session" as const)
                                        : ("Last reported" as const),
                                  }
                                : {}),
                            })
                          }
                          aria-label={`Configuration preview for browser session ${session.id}`}
                        >
                          <Monitor size={16} /> Configuration preview
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() => {
                          setAssignBrowserSessionId(session.id);
                          setAssignBrowserTvId(session.tvId ?? "");
                          setNotice("");
                        }}
                        aria-label={`${session.tvId ? "Reassign" : "Assign"} browser session ${session.id}`}
                      >
                        <Link2 size={16} /> {session.tvId ? "Reassign" : "Assign"}
                      </button>
                      <button
                        type="button"
                        className="button button--danger button--small"
                        onClick={() => {
                          setRevokeBrowserSessionId(session.id);
                          setNotice("");
                        }}
                        aria-label={`Revoke browser session ${session.id}`}
                      >
                        <ShieldOff size={16} /> Revoke
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!browserSessionsQuery.isLoading && !(browserSessionsQuery.data ?? []).length ? (
            <p className="empty-copy">
              No active browser sessions have contacted this deployment yet.
            </p>
          ) : null}
        </div>
      </section>

      <section className="card table-card">
        <div className="section-heading">
          <div>
            <h3>Managed TVs</h3>
            <p>
              Review each assigned TV’s cloud presence, last contact, software, update status, and
              remote administration actions.
            </p>
          </div>
        </div>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>TV</th>
                <th>Device name</th>
                <th>Cloud</th>
                <th>Last contact</th>
                <th>APK / Android</th>
                <th>Update</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {(query.data ?? []).map((tv) => (
                <tr key={tv.id}>
                  <td>
                    <strong>{tv.name}</strong>
                    <small className="cell-subtitle">{tv.clientName}</small>
                    {tv.installationId ? (
                      <small className="cell-subtitle">
                        Device ID <code>{tv.installationId}</code>
                      </small>
                    ) : null}
                  </td>
                  <td>
                    {tv.installationId ? (tv.deviceName ?? "Unnamed device") : "Not enrolled"}
                  </td>
                  <td>
                    <span className={`status-pill status-${tv.cloudStatus.replaceAll(" ", "-")}`}>
                      {tv.cloudStatus === "online" ? <Cloud size={14} /> : <CloudOff size={14} />}
                      {tv.cloudStatus}
                    </span>
                  </td>
                  <td>{formatContact(tv.lastContactAt)}</td>
                  <td>
                    {tv.apkVersion ?? "Unknown"}
                    <small className="cell-subtitle">
                      {tv.androidVersion ?? "Unknown Android"}
                    </small>
                  </td>
                  <td>{tv.updateStatus ?? "Not reported"}</td>
                  <td>
                    <div className="action-row">
                      {tv.installationId ? (
                        <>
                          <button
                            type="button"
                            className="button button--secondary button--small"
                            onClick={() =>
                              openRename({
                                kind: "tv",
                                id: tv.id,
                                deviceId: tv.installationId!,
                                name: tv.deviceName,
                              })
                            }
                            aria-label={`Rename device for ${tv.name}`}
                          >
                            <Pencil size={16} /> Name
                          </button>
                          <button
                            type="button"
                            className="button button--secondary button--small"
                            onClick={() =>
                              identifyDevice.mutate({
                                kind: "tv",
                                id: tv.id,
                                deviceId: tv.installationId!,
                              })
                            }
                            disabled={identifyDevice.isPending}
                            aria-label={`Identify device for ${tv.name}`}
                          >
                            <ScanLine size={16} /> Identify
                          </button>
                        </>
                      ) : null}
                      {tv.cloudStatus === "online" && tv.installationId ? (
                        <button
                          type="button"
                          className="button button--secondary button--small"
                          onClick={() =>
                            setPreviewTarget({
                              tvId: tv.id,
                              name: tv.name,
                              description: `${tv.clientName} · claimed Device ID ${tv.installationId}`,
                            })
                          }
                          aria-label={`Configuration preview for ${tv.name}`}
                        >
                          <Monitor size={16} /> Configuration preview
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="button button--secondary button--small"
                        onClick={() => {
                          setSelectedId(tv.id);
                          setPendingAdminId(null);
                          setReconnectDeviceId(tv.installationId ?? "");
                          setReconnectConfirmation("");
                          setNotice("");
                        }}
                        aria-label={`Remote Admin for ${tv.name}`}
                      >
                        <TerminalSquare size={16} /> Remote Admin
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!query.isLoading && !(query.data ?? []).length ? (
            <p className="empty-copy">No active TVs are available.</p>
          ) : null}
        </div>
      </section>

      {previewTarget ? (
        <Modal
          title={`Configuration preview · ${previewTarget.name}`}
          description={`${previewTarget.description}. This renders the assigned configuration at the selected device dimensions.`}
          onClose={() => setPreviewTarget(null)}
        >
          <div className="device-live-preview-dialog">
            <div className="section-heading">
              <div>
                <h3>Assigned configuration</h3>
                <p>
                  Content and scheduling refresh every five seconds. Slides rotate locally using
                  their saved durations.
                </p>
              </div>
              <button
                type="button"
                className="button button--secondary button--small"
                onClick={() => void previewQuery.refetch()}
                disabled={previewQuery.isFetching}
              >
                <RefreshCw size={16} /> Refresh configuration
              </button>
            </div>
            {previewQuery.isLoading ? (
              <section className="card inset-card">Loading configuration preview…</section>
            ) : null}
            {previewQuery.error ? (
              <p className="notice error">{errorMessage(previewQuery.error)}</p>
            ) : null}
            {previewQuery.data ? (
              <DeviceLivePreview
                configuration={previewQuery.data}
                {...(previewTarget.reportedViewport
                  ? { reportedViewport: previewTarget.reportedViewport }
                  : {})}
                {...(previewTarget.reportedViewportLabel
                  ? { reportedViewportLabel: previewTarget.reportedViewportLabel }
                  : {})}
              />
            ) : null}
          </div>
        </Modal>
      ) : null}

      {wireframeTarget ? (
        <Modal
          title={`Live wireframe · ${wireframeTarget.name}`}
          description="A single geometry-only snapshot is requested from this browser session. Nothing is streamed and no screen pixels or content values are collected."
          onClose={closeWireframe}
        >
          <div className="browser-wireframe-dialog">
            <div className="section-heading">
              <div>
                <h3>Current browser layout</h3>
                <p>
                  The request is delivered through the authenticated browser heartbeat and reports
                  only viewport, state, layers, element types, and visible bounds.
                </p>
              </div>
              <button
                type="button"
                className="button button--secondary button--small"
                onClick={() => openWireframe(wireframeTarget)}
                disabled={requestWireframe.isPending}
              >
                <RefreshCw size={16} /> Request again
              </button>
            </div>
            {requestWireframe.isPending ? (
              <section className="card inset-card" role="status">
                Queuing wireframe request…
              </section>
            ) : null}
            {requestWireframe.error ? (
              <p className="notice error" role="alert">
                {errorMessage(requestWireframe.error)}
              </p>
            ) : null}
            {wireframeQuery.error ? (
              <p className="notice error" role="alert">
                {errorMessage(wireframeQuery.error)}
              </p>
            ) : null}
            {wireframeQuery.data && wireframeQuery.data.status !== "ready" ? (
              <section className="card inset-card" role="status" aria-live="polite">
                {wireframeQuery.data.status === "delivered"
                  ? "Request delivered. Waiting for the browser to return its wireframe…"
                  : "Waiting for the browser session’s next heartbeat…"}
              </section>
            ) : null}
            {wireframeQuery.data?.status === "ready" && wireframeQuery.data.wireframe ? (
              <BrowserSessionWireframePreview wireframe={wireframeQuery.data.wireframe} />
            ) : null}
          </div>
        </Modal>
      ) : null}

      {renameTarget ? (
        <Modal
          title="Name Device Cloud device"
          description={`Device ID ${renameTarget.deviceId}`}
          onClose={() => {
            setRenameTarget(null);
            setDeviceName("");
          }}
        >
          <form
            className="card inset-card form-stack"
            onSubmit={(event) => {
              event.preventDefault();
              setNotice("");
              renameDevice.mutate();
            }}
          >
            {renameDevice.error ? (
              <p className="notice error">{errorMessage(renameDevice.error)}</p>
            ) : null}
            <label>
              Device name
              <input
                value={deviceName}
                onChange={(event) => setDeviceName(event.target.value)}
                maxLength={80}
                required
                autoFocus
                placeholder="Reception display"
              />
            </label>
            <p>
              This admin-only label identifies the physical device. It does not rename the client’s
              assigned TV.
            </p>
            <div className="action-row">
              <button
                className="button button--primary"
                type="submit"
                disabled={!deviceName.trim() || renameDevice.isPending}
              >
                <Pencil size={17} /> Save device name
              </button>
              <button
                className="button button--secondary"
                type="button"
                onClick={() => {
                  setRenameTarget(null);
                  setDeviceName("");
                }}
                disabled={renameDevice.isPending}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {assigningBrowserSession ? (
        <Modal
          title="Assign browser session"
          description={`Browser device ${assigningBrowserSession.id}`}
          onClose={() => {
            setAssignBrowserSessionId(null);
            setAssignBrowserTvId("");
          }}
        >
          <form
            className="card inset-card form-stack"
            onSubmit={(event) => {
              event.preventDefault();
              setNotice("");
              assignBrowserSession.mutate();
            }}
          >
            {assignBrowserSession.error ? (
              <p className="notice error">{errorMessage(assignBrowserSession.error)}</p>
            ) : null}
            <label>
              Existing TV
              <select
                value={assignBrowserTvId}
                onChange={(event) => setAssignBrowserTvId(event.target.value)}
                required
              >
                <option value="">Select a TV</option>
                {(query.data ?? [])
                  .filter((tv) => tv.status === "active")
                  .map((tv) => (
                    <option key={tv.id} value={tv.id}>
                      {tv.clientName} — {tv.name}
                    </option>
                  ))}
              </select>
            </label>
            {assigningBrowserSession.tvId &&
            assignBrowserTvId &&
            assigningBrowserSession.tvId !== assignBrowserTvId ? (
              <p className="notice warning">
                This reassigns only this browser session. Its current player credential will begin
                loading the selected TV; other browsers assigned to the previous TV are unchanged.
              </p>
            ) : (
              <p>
                The browser will receive its private player access through its authenticated
                heartbeat. No pairing code or APK is required.
              </p>
            )}
            <div className="action-row">
              <button
                className="button"
                type="submit"
                disabled={!assignBrowserTvId || assignBrowserSession.isPending}
              >
                <Link2 size={17} />{" "}
                {assigningBrowserSession.tvId ? "Save assignment" : "Assign session"}
              </button>
              <button
                className="button button--secondary"
                type="button"
                onClick={() => {
                  setAssignBrowserSessionId(null);
                  setAssignBrowserTvId("");
                }}
                disabled={assignBrowserSession.isPending}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {revokingBrowserSession ? (
        <Modal
          title="Revoke browser session"
          description={`Browser device ${revokingBrowserSession.id}`}
          onClose={() => setRevokeBrowserSessionId(null)}
        >
          <section className="card inset-card form-stack">
            {revokeBrowserSession.error ? (
              <p className="notice error">{errorMessage(revokeBrowserSession.error)}</p>
            ) : null}
            <p className="notice warning">
              Revoking invalidates this browser session and its linked player credential. Playback
              stops after the next server check. If the browser remains open, it will register a new
              unclaimed session that can be assigned again.
            </p>
            <div className="action-row">
              <button
                className="button button--danger"
                type="button"
                onClick={() => revokeBrowserSession.mutate()}
                disabled={revokeBrowserSession.isPending}
              >
                <ShieldOff size={17} /> Revoke browser session
              </button>
              <button
                className="button button--secondary"
                type="button"
                onClick={() => setRevokeBrowserSessionId(null)}
                disabled={revokeBrowserSession.isPending}
              >
                Cancel
              </button>
            </div>
          </section>
        </Modal>
      ) : null}

      {pendingDevice ? (
        <Modal
          title="Claim Q-Assist device"
          description={`Device ID ${pendingDevice.deviceId}`}
          onClose={() => setPendingDeviceId(null)}
        >
          <form
            className="card inset-card form-stack"
            onSubmit={(event) => {
              event.preventDefault();
              setNotice("");
              claimPending.mutate();
            }}
          >
            {claimPending.error ? (
              <p className="notice error">{errorMessage(claimPending.error)}</p>
            ) : null}
            <label>
              Claim using
              <select
                value={pendingClaimMethod}
                onChange={(event) =>
                  setPendingClaimMethod(event.target.value as "tvId" | "pairingCode")
                }
              >
                <option value="tvId">Existing TV assignment</option>
                <option value="pairingCode">Six-digit pairing code</option>
              </select>
            </label>
            {pendingClaimMethod === "tvId" ? (
              <label>
                Client and TV
                <select
                  value={pendingClaimTvId}
                  onChange={(event) => setPendingClaimTvId(event.target.value)}
                  required
                >
                  <option value="">Select a TV</option>
                  {(query.data ?? [])
                    .filter((tv) => tv.status === "active")
                    .map((tv) => (
                      <option key={tv.id} value={tv.id}>
                        {tv.clientName} — {tv.name}
                      </option>
                    ))}
                </select>
              </label>
            ) : (
              <label>
                Six-digit pairing code
                <input
                  value={pendingPairingCode}
                  onChange={(event) =>
                    setPendingPairingCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  inputMode="numeric"
                  autoComplete="off"
                  pattern="[0-9]{6}"
                  minLength={6}
                  maxLength={6}
                  required
                />
              </label>
            )}
            <p>
              The claim associates this physical APK installation with the selected client TV. The
              device retrieves its private cloud credential on its next poll.
            </p>
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={pendingTransfer}
                onChange={(event) => setPendingTransfer(event.target.checked)}
              />
              <span>
                Explicitly transfer the TV if it already belongs to another APK installation
              </span>
            </label>
            {pendingTransfer ? (
              <p className="notice warning">
                Transfer revokes the previous installation’s cloud credential and replaces its
                Device ID association. It does not change browser-player pairing or erase the TV.
              </p>
            ) : null}
            <div className="action-row">
              <button className="button" type="submit" disabled={claimPending.isPending}>
                <Cloud size={17} /> Claim device
              </button>
              <button
                className="button button--secondary"
                type="button"
                onClick={() => setPendingDeviceId(null)}
                disabled={claimPending.isPending}
              >
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {remoteTarget ? (
        <Modal
          title={`Remote Admin · ${remoteTarget.name}`}
          description={remoteTarget.description}
          onClose={() => {
            setSelectedId(null);
            setPendingAdminId(null);
            setReconnectDeviceId("");
            setReconnectConfirmation("");
          }}
        >
          <div className="device-cloud-admin">
            {notice ? <p className="notice success">{notice}</p> : null}
            {activeError ? <p className="notice error">{errorMessage(activeError)}</p> : null}

            {remoteTarget.kind === "tv" && remoteTarget.status === "not enrolled" ? (
              <form
                className="card inset-card form-stack"
                onSubmit={(event) => {
                  event.preventDefault();
                  claim.mutate();
                }}
              >
                <div>
                  <h3>Enroll this APK</h3>
                  <p>
                    Enter the eight-character claim code shown in the TV’s local panel diagnostics.
                  </p>
                </div>
                <label>
                  Claim code
                  <input
                    value={claimCode}
                    onChange={(event) => setClaimCode(event.target.value.toUpperCase())}
                    minLength={8}
                    maxLength={8}
                    pattern="[A-Z0-9]{8}"
                    autoComplete="off"
                    required
                  />
                </label>
                <button className="button" type="submit" disabled={claim.isPending}>
                  <Cloud size={17} /> Claim enrollment
                </button>
              </form>
            ) : (
              <>
                <section className="card inset-card">
                  <div className="section-heading">
                    <div>
                      <h3>Tailscale VPN</h3>
                      <p>Current device-reported state. Use Read diagnostics to refresh it.</p>
                    </div>
                  </div>
                  <dl className="device-cloud-status-grid">
                    {(
                      [
                        ["Status", "Tailscale status"],
                        ["Always-on VPN", "Tailscale always-on VPN"],
                        ["Authorization", "Tailscale authorization"],
                        ["Exit node", "Tailscale exit node status"],
                        ["Installation", "Tailscale install status"],
                      ] as const
                    ).map(([label, key]) => (
                      <div key={key}>
                        <dt>{label}</dt>
                        <dd>{diagnosticText(remoteTarget.diagnostics, key)}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
                <form className="card inset-card form-stack" onSubmit={submitCommand}>
                  <label>
                    Remote operation
                    <select
                      value={commandType}
                      onChange={(event) => setCommandType(event.target.value as CommandType)}
                    >
                      {Object.entries(commandLabels)
                        .filter(([value]) => value !== "identify_device")
                        .map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                    </select>
                  </label>
                  {commandType === "connect_wifi" ? (
                    <div className="form-grid">
                      <label>
                        Wi-Fi name (SSID)
                        <input
                          value={form.wifiSsid}
                          onChange={(event) =>
                            setForm((current) => ({ ...current, wifiSsid: event.target.value }))
                          }
                          maxLength={32}
                          required
                        />
                      </label>
                      <label>
                        Wi-Fi password
                        <input
                          type="password"
                          value={form.wifiPassword}
                          onChange={(event) =>
                            setForm((current) => ({ ...current, wifiPassword: event.target.value }))
                          }
                          maxLength={63}
                          autoComplete="new-password"
                        />
                      </label>
                    </div>
                  ) : null}
                  {commandType === "update_settings" ? (
                    <div className="form-stack">
                      <p className="field-help">
                        Override any of the APK’s three public addresses on this device. Blank
                        fields remain unchanged. The player home URL is also the same-origin base
                        used for player and Device Cloud API requests.
                      </p>
                      <div className="form-grid">
                        <label>
                          Player home and API URL
                          <input
                            type="url"
                            value={form.homeUrl}
                            onChange={(event) =>
                              setForm((current) => ({ ...current, homeUrl: event.target.value }))
                            }
                            placeholder="https://tvplayer.qassist.qa"
                          />
                        </label>
                        <label>
                          APK update metadata URL
                          <input
                            type="url"
                            value={form.updateMetadataUrl}
                            onChange={(event) =>
                              setForm((current) => ({
                                ...current,
                                updateMetadataUrl: event.target.value,
                              }))
                            }
                            placeholder="https://tvplayer.qassist.qa/downloads/android-tv/metadata.json"
                          />
                        </label>
                        <label>
                          APK file URL
                          <input
                            type="url"
                            value={form.updateApkUrl}
                            onChange={(event) =>
                              setForm((current) => ({
                                ...current,
                                updateApkUrl: event.target.value,
                              }))
                            }
                            placeholder="https://tvplayer.qassist.qa/downloads/android-tv/qassist-tv.apk"
                          />
                        </label>
                        <label>
                          Local-panel port
                          <input
                            type="number"
                            min={1024}
                            max={65535}
                            value={form.panelPort}
                            onChange={(event) =>
                              setForm((current) => ({ ...current, panelPort: event.target.value }))
                            }
                            placeholder="Leave unchanged"
                          />
                        </label>
                        <label>
                          Hotspot SSID
                          <input
                            maxLength={32}
                            value={form.hotspotSsid}
                            onChange={(event) =>
                              setForm((current) => ({
                                ...current,
                                hotspotSsid: event.target.value,
                              }))
                            }
                            placeholder="Leave unchanged"
                          />
                        </label>
                        <label>
                          Hotspot password
                          <input
                            type="password"
                            minLength={8}
                            maxLength={63}
                            value={form.hotspotPassword}
                            onChange={(event) =>
                              setForm((current) => ({
                                ...current,
                                hotspotPassword: event.target.value,
                              }))
                            }
                            autoComplete="new-password"
                            placeholder="Leave unchanged"
                          />
                        </label>
                      </div>
                    </div>
                  ) : null}
                  {commandType === "update_device_access" ? (
                    <div className="form-grid">
                      {(
                        [
                          ["allowAndroidSettings", "Android settings"],
                          ["allowUsbDebugging", "USB debugging"],
                          ["allowAppUninstall", "App uninstall"],
                          ["allowAppInstall", "Local APK installation"],
                        ] as const
                      ).map(([key, label]) => (
                        <label key={key}>
                          {label}
                          <select
                            value={form[key]}
                            onChange={(event) =>
                              setForm((current) => ({ ...current, [key]: event.target.value }))
                            }
                          >
                            <option value="">Leave unchanged</option>
                            <option value="false">Block</option>
                            <option value="true">Allow</option>
                          </select>
                        </label>
                      ))}
                      <p className="help">
                        USB debugging, uninstall, and install restrictions are enforced only when
                        Q-Assist is Android Device Owner. They define steady-state policy and never
                        disable authenticated Local Panel or Device Cloud actions. Device Cloud
                        never accepts APK files.
                      </p>
                    </div>
                  ) : null}
                  {commandType === "authorize_tailscale" ? (
                    <p className="help">
                      This opens Tailscale on the television. Select Log in there, then enter the
                      generated Android TV code in Tailscale Admin Console → Machines → Add device →
                      Android → Input code. Q-Assist never receives or stores the code.
                    </p>
                  ) : null}
                  {commandType === "set_tailscale_always_on" ? (
                    <label>
                      Always-on VPN
                      <select
                        value={form.tailscaleAlwaysOn}
                        onChange={(event) =>
                          setForm((current) => ({
                            ...current,
                            tailscaleAlwaysOn: event.target.value,
                          }))
                        }
                      >
                        <option value="true">On</option>
                        <option value="false">Off</option>
                      </select>
                    </label>
                  ) : null}
                  {commandType === "install_tailscale" ? (
                    <p className="notice warning">
                      This dedicated operation works regardless of the generic Device access
                      switches. The APK downloads only Tailscale’s official universal Android
                      package, verifies its published checksum and package ID, and temporarily
                      clears a Device Owner install restriction only for that package.
                    </p>
                  ) : null}
                  {commandType === "authorize_tailscale" ? (
                    <p className="notice warning">
                      Requires Android 8 or later and access to the television screen plus the
                      Tailscale admin console.
                    </p>
                  ) : null}
                  {commandType === "set_tailscale_always_on" ? (
                    <p className="notice warning">
                      Requires Android 7 or later and Q-Assist Device Owner provisioning. Lockdown
                      mode is not enabled.
                    </p>
                  ) : null}
                  {commandType === "open_android_settings" ? (
                    <p className="notice warning">
                      Android settings opens only when Android settings access is allowed above.
                    </p>
                  ) : null}
                  {commandType === "reboot" ? (
                    <p className="notice warning">
                      Reboot succeeds only when Q-Assist is provisioned as Android Device Owner.
                    </p>
                  ) : null}
                  <button className="button" type="submit" disabled={queue.isPending}>
                    <Send size={17} /> Queue {commandLabels[commandType]}
                  </button>
                </form>

                {remoteTarget.kind === "tv" ? (
                  <section className="card inset-card">
                    <div className="section-heading">
                      <div>
                        <h3>Credential controls</h3>
                        <p>
                          Rotation replaces cloud access in place. Revoke and release invalidates
                          old access, cancels pending commands, and returns the APK to the
                          unassigned list after it reconnects.
                        </p>
                      </div>
                      <div className="action-row">
                        <button
                          className="button button--secondary button--small"
                          type="button"
                          onClick={() => rotate.mutate()}
                          disabled={rotate.isPending}
                        >
                          <RotateCw size={16} /> Rotate
                        </button>
                        <button
                          className="button button--danger button--small"
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Revoke cloud access for ${remoteTarget.name} and release its Device ID? The APK must be claimed again before remote commands can be sent.`,
                              )
                            )
                              revoke.mutate();
                          }}
                          disabled={revoke.isPending}
                          aria-label={`Revoke and release ${remoteTarget.name}`}
                        >
                          <ShieldOff size={16} /> Revoke &amp; release
                        </button>
                      </div>
                    </div>
                    <form
                      className="form-stack device-cloud-reconnect"
                      onSubmit={(event) => {
                        event.preventDefault();
                        setNotice("");
                        reconnect.mutate();
                      }}
                    >
                      <h4>Reconnect an existing installation</h4>
                      <p>
                        Use this only when the physical TV’s displayed Device ID is already claimed
                        to this TV but its credential no longer authenticates.
                      </p>
                      <label>
                        Physical TV Device ID
                        <input
                          value={reconnectDeviceId}
                          onChange={(event) => setReconnectDeviceId(event.target.value)}
                          pattern="[0-9a-fA-F-]{36}"
                          minLength={36}
                          maxLength={36}
                          required
                        />
                      </label>
                      <label>
                        Type RECONNECT Q-ASSIST DEVICE
                        <input
                          value={reconnectConfirmation}
                          onChange={(event) => setReconnectConfirmation(event.target.value)}
                          required
                        />
                      </label>
                      <button
                        className="button button--secondary button--small"
                        type="submit"
                        disabled={
                          reconnect.isPending ||
                          reconnectConfirmation !== "RECONNECT Q-ASSIST DEVICE"
                        }
                      >
                        <RotateCw size={16} /> Reconnect installation
                      </button>
                    </form>
                  </section>
                ) : null}
              </>
            )}

            <section className="card inset-card">
              <h3>Current diagnostics</h3>
              <pre className="device-cloud-json">
                {JSON.stringify(remoteTarget.diagnostics ?? {}, null, 2)}
              </pre>
            </section>

            <section className="card inset-card">
              <h3>Command history</h3>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Requested</th>
                      <th>Command</th>
                      <th>Status</th>
                      <th>Attempts</th>
                      <th>Result</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {remoteTarget.commands.map((command) => (
                      <tr key={command.id}>
                        <td>{new Date(command.createdAt).toLocaleString()}</td>
                        <td>{commandLabels[command.type]}</td>
                        <td>
                          <span className={`status-pill status-${command.status}`}>
                            {command.status}
                          </span>
                        </td>
                        <td>{command.attempts}</td>
                        <td>{command.result?.message ?? command.errorCode ?? "Pending"}</td>
                        <td>
                          {command.status === "queued" || command.status === "delivered" ? (
                            <button
                              className="button button--secondary button--small"
                              type="button"
                              onClick={() => cancel.mutate(command.id)}
                              disabled={cancel.isPending}
                              aria-label={`Cancel ${commandLabels[command.type]} command`}
                            >
                              Cancel
                            </button>
                          ) : (
                            "—"
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!remoteTarget.commands.length ? (
                  <p className="empty-copy">No remote commands have been requested.</p>
                ) : null}
              </div>
            </section>
          </div>
        </Modal>
      ) : null}
    </div>
  );
}
